export {Interval} from "../../../../../Terrain/Heights/IsolineTypes.js";
import {O} from "web-vcore";
import {format_ObservableArray} from "../../../../../Utils/General/MobX.js";
import {
	PoissonSolverConfig,
	PathTerrainFeature,
} from "../../../../../Terrain/Heights/IsolineTypes.js";
import type {PathTerrainPinShape} from "../../../../../Terrain/Heights/IsolineTypes.js";
export {PoissonSolverConfig, PathTerrainFeature};
export type {PathTerrainPinShape};

export class Mod_TerrainDefinition {
	constructor(data?: Partial<Mod_TerrainDefinition>) { Object.assign(this, data); }
	terrainID = "";
	/** World seed; combined with per-isoline UUIDs for all randomness. */
	seed = 0;
	ambient = new TerrainAmbient();
	constraintSettings = new TerrainConstraintSettings();
	// Named hypsometric color-gradients; terrain applies the "default" one (see TerrainHeightColorGradient.GetDefault).
	heightColorGradients = [new TerrainHeightColorGradient({name: "Default", isDefault: true})];
	heightShadingConfig? = new TerrainHeightShadingConfig();
}

export class TerrainAmbient {
	constructor(data?: Partial<TerrainAmbient>) { Object.assign(this, data); }
	algorithm: "opensimplex2" | "perlin" | "value" = "opensimplex2";
	fractalType: "fbm" | "ridged" | "billow" = "fbm";
	wavelength = 1000;
	octaves = 6;
	persistence = 0.5;
	lacunarity = 2.0;
	offset_x = 0.0;
	offset_y = 0.0;
	/** **Center model (2026-08-31): the CENTER of the ambient noise range — where "flat" terrain
	 *  sits.** The ambient fractal oscillates ± `amplitude/2` around this value, so the range is
	 *  `[base − amp/2, base + amp/2]` and its mean/DC elevation IS `base`. The L (large-wave) band
	 *  evaluates `base + frac·amp` (frac ∈ [−0.5, +0.5]); an empty L band returns exactly `base`.
	 *  Set 0 for sea-level baseline. */
	baseElevation = 0.0;
	/** Total amplitude of ambient relief. Keep small; ~gentle hills. */
	amplitude = 60.0;
	/** **2026-08-07 — the L/D (large-wave vs detail) band split threshold, in METERS.** Octaves whose
	 *  wavelength is <= this value are the DETAIL band (D — applied on top everywhere, never muted near
	 *  elevation pins); octaves whose wavelength is larger are the LARGE-WAVE band (L — the base relief,
	 *  muted near pins when `dampenAmbientWaves_large` is on). Direct replacement for the old
	 *  `lodSplitFactor` (a wavelength/lodSplitFactor-derived threshold) — this is a plain meters value,
	 *  so the user can set it directly ("below this size = detail"). Absent → falls back to
	 *  `wavelength / 2` (the old lodSplitFactor=2 default — existing maps render identically until set). */
	maxWavelengthAsDetail?: number;
	/** **Set10 — gain for the fragment-shader ambient detail normal (the sub-lattice band).**
	 *  Decoupled from `amplitude` because the D band's share of `amplitude` collapses as the
	 *  octave index rises (see Set10 §3.4 — the D band normalises against the TOTAL amp sum, so
	 *  its peak relief decays by persistence^octaveIndex).  1 = use the physically-consistent
	 *  share (the octaves' true amplitudes); >1 exaggerates fine relief.  Absent → 1 → a
	 *  no-op-by-default field: existing maps do not change look on upgrade beyond the new band
	 *  appearing.  Set 0 to disable the shader band entirely (an exact no-op — bisection aid). */
	detailNormalStrength?: number;
}

export class TerrainConstraintSettings {
	constructor(data?: Partial<TerrainConstraintSettings>) { Object.assign(this, data); }

	/** Curve sample spacing = clamp(curveLength * factor, min, max).
	 *  V3: ambient-noise L/D-split spacing only; decoupled from Poisson cell size. */
	sampleSpacingFactor = 1 / 40;
	sampleSpacingMin = 20;   // meters
	sampleSpacingMax = 80;   // meters

	/** Detail damping band scaling constant; tunes default band width (4 × fine cell size × dampingK). */
	dampingK = 2.5;

	/** V3: Poisson/AMR solver configuration. When present (V3 mode), the engine uses the
	 *  Hnaidi-Poisson + AMR two-grid path. Phase 7 removes the absent-config V2 RBF fallback
	 *  entirely — if this is undefined at runtime, the engine constructs a default config. */
	poissonSolver?: PoissonSolverConfig;

	/** Resolve a PoissonSolverConfig, default-constructing one when unset (Phase 7: V3 is the
	 *  only terrain path; the V2 RBF fallback was removed). */
	static ResolvePoisson(settings: TerrainConstraintSettings): PoissonSolverConfig {
		return settings.poissonSolver ?? new PoissonSolverConfig();
	}
}

/** Optional detail-character modulation near constraints (phase 7+). */
export class NearConstraintModulation {
	constructor(data?: Partial<NearConstraintModulation>) { Object.assign(this, data); }
	enabled = false;
	/** Multiplier on D amplitude on the uphill side of constraints (0 = off). */
	amplitudeSwell = 0.5;
	/** Blend toward ridged fractal on the uphill side (0..1). */
	ridgedBlend = 0.35;
}

export class TerrainHeightColorGradient {
	constructor(data?: Partial<TerrainHeightColorGradient>) {  Object.assign(this, data); }
	/** Display name shown in the gradient-picker UI. */
	name = "Untitled";
	// Marks this as the gradient the terrain system applies. UI enforces ≤1 true; GetDefault falls back to index 0.
	isDefault = false;
	@format_ObservableArray(()=>new HypsometricStop())
	stops = DEFAULT_HYPSOMETRIC_STOPS.map(s=>new HypsometricStop(s));

	/** Returns the "default" gradient from a list: the first with `isDefault === true`, else index 0. */
	static GetDefault(gradients: TerrainHeightColorGradient[]): TerrainHeightColorGradient {
		return gradients.find(g=>g.isDefault) ?? gradients[0];
	}
}

export class TerrainHeightShadingConfig {
	light_azimuth = 315.0;
	light_altitude = 45.0;
	ambient = 0.2;
	strength = 1.0;
	/** Set15 P2.5 (D3) — RETIRED.  Fixed at 1.0: displacing by `slopeScale·h` would render a
	 *  2200 m peak at `slopeScale`× the height, so elevation pins would no longer match the
	 *  rendered geometry.  At 1.0 the worker's central-difference normals ARE the true surface
	 *  normals, so shading and silhouette agree and the GGX/PBR path is physically meaningful.
	 *  The shader seam (`u_slopeScale` uniform + `matAmbientSlopeScale()`) is RETAINED pinned at
	 *  1.0 so re-adding the knob is a one-line change.  Existing persisted maps with
	 *  `slope_scale: 4` are silently dropped (the renderer pins this to 1.0 regardless —
	 *  `viewRefinementPadding` rename precedent).  The field stays for schema compat; callers
	 *  must read the pinned value, never this field. */
	slope_scale = 1.0;
}

export class HypsometricStop {
	constructor(data?: Partial<HypsometricStop>) {  Object.assign(this, data); }
	elevation = 0;
	// use O.ref, so when ColorPickerBox tries to read the 4th/alpha value, mobx doesn't log a warning
	color: [number,number,number] = [0, 0, 0];
}

export const DEFAULT_HYPSOMETRIC_STOPS: Array<{elevation: number, color: [number,number,number]}> = [
	{elevation: -500, color: [10,  30,  80]},
	{elevation: -1,   color: [30,  80,  160]},
	{elevation: 0,    color: [210, 185, 120]},
	{elevation: 80,   color: [140, 185, 100]},
	{elevation: 500,  color: [80,  130, 60]},
	{elevation: 1200, color: [130, 100, 60]},
	{elevation: 2200, color: [160, 140, 130]},
	{elevation: 3000, color: [240, 240, 255]},
];